import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAsync } from '../../utils/useAsync';
import { DriverFormDataSubmit, useDriverFormService } from '../../services/driverFormService';
import { useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {DriverFormData, driverSchema, eighteenYearsAgo, RawDriverFormData} from './driverForm.schema';

export const useDriverFormController = () => {
  const { uploadFile, submitDriverForm } = useDriverFormService();
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const toast = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    reset,
    setError,
    trigger,
    clearErrors,
    watch,
  } = useForm<DriverFormData>({
    resolver: yupResolver(driverSchema),
    mode: 'onChange',
  });

  // #####################
  // Submit Logic
  // #####################

  const mapToDriverFormData = (
    form: RawDriverFormData
  ): DriverFormDataSubmit => {
    return {
      userData: {
        email: `${form.dni}@viajaya.app`,
        userEmail: form.email,
        password: '',
        profile: {
          firstname: form.firstName,
          lastname: form.lastName,
          location: {
            province: form.state,
            city: form.city,
          },
          gender: form.gender as 'male' | 'female' | 'other',
          photoUrl: form.profilePhoto[0]?.url || '',
          birthDate: new Date(form.birthDate).toISOString(),
          billingData: {
            documentNumber: form.dni,
            phoneNumber: form.phone,
          },
        },
        documents: [
          {
            name: 'document-front',
            url: form.dniFrontPhoto[0]?.url,
          },
          {
            name: 'document-back',
            url: form.dniBackPhoto[0]?.url,
          },
          {
            name: 'license-front',
            url: form.licenseFrontPhoto[0]?.url,
          },
          {
            name: 'license-back',
            url: form.licenseBackPhoto[0]?.url,
          },
          {
            name: 'cedula-front',
            url: form.vehicleRegistrationFrontPhoto[0]?.url,
          },
          {
            name: 'cedula-back',
            url: form.vehicleRegistrationBackPhoto[0]?.url,
          },
        ],
      },
      vehicleData: {
        brand: form.brand,
        model: form.model,
        year: form.year,
        color: form.color,
        patent: form.licensePlate,
        type: form.vehicleType,
      },
    };
  };

  const onSubmit = async (formData: RawDriverFormData) => {
    try {
      const isValid = await trigger();
      if (!isValid) return;
      const data = mapToDriverFormData(formData);
      executeSubmitDriverForm(data);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const [, ,loadingSubmit , executeSubmitDriverForm] = useAsync(submitDriverForm, {
    onSuccess() {
      toast({
        title: 'Gracias por registrarte en ViajáYa',
        description:
          'Estamos revisando tu solicitud. Pronto te escribiremos por WhatsApp.',
        status: 'success',
        duration: 15000,
        isClosable: true,
        position: 'top-right',
        containerStyle: {
          zIndex: 99999,
         
        },
      });
      reset();
      setTimeout(() => {
        navigate('/');
      }, 15000);
    },
    onError(error) {
      let errorMessage = 'Ha ocurrido un error al registrar el conductor';
      if (error.message === 'User already exists') {
        errorMessage = 'El conductor se encuentra registrado';
      }
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const validateFileFields = () => {
    const fileFields = [
      'dniFrontPhoto',
      'dniBackPhoto',
      'licenseFrontPhoto',
      'licenseBackPhoto',
      'profilePhoto',
      'vehicleRegistrationFrontPhoto',
      'vehicleRegistrationBackPhoto',
    ] as const;

    fileFields.forEach((field) => {
      const value = getValues(field);
      if (!Array.isArray(value)) {
        setValue(field, []);
      } else {
        const validFiles = value.filter(file => {
          return file && file.url && file.url !== '';
        });
        setValue(field, validFiles);
      }
    });
  };

  // ######################
  // Upload File
  // ######################

  const [, , , executeUploadFile] = useAsync(uploadFile, {
    onSuccess(data) {
      setUploadingFiles((prev) => ({
        ...prev,
        [data.fileName]: false,
      }));

      switch (data.fileName) {
        case 'dniFrontPhoto':
          setValue('dniFrontPhoto', [
            {
              url: data.url,
              name: 'document-front',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'dniBackPhoto':
          setValue('dniBackPhoto', [
            {
              url: data.url,
              name: 'document-back',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'licenseFrontPhoto':
          setValue('licenseFrontPhoto', [
            {
              url: data.url,
              name: 'license-front',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'licenseBackPhoto':
          setValue('licenseBackPhoto', [
            {
              url: data.url,
              name: 'license-back',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'vehicleRegistrationFrontPhoto':
          setValue('vehicleRegistrationFrontPhoto', [
            {
              url: data.url,
              name: 'cedula-front',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'vehicleRegistrationBackPhoto':
          setValue('vehicleRegistrationBackPhoto', [
            {
              url: data.url,
              name: 'cedula-back',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
        case 'profilePhoto':
          setValue('profilePhoto', [
            {
              url: data.url,
              name: 'profile',
              previewURL: data.url,
              id: data._id,
            },
          ]);
          break;
      }

    },
  });

  return {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    setValue,
    getValues,
    reset,
    setError,
    trigger,
    clearErrors,
    watch,
    uploadingFiles,
    setUploadingFiles,
    executeUploadFile,
    onSubmit,
    validateFileFields,
    eighteenYearsAgo,
    loadingSubmit,
  };
};
